import { formatTimestamp } from '../../utils/timestamps';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Typography,
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import EntryTypeIcon from './EntryTypeIcon';
import { prettifyEntryKey } from '../../utils/widgetModel';
import { formatText } from './TextFormatterProvider';
import PreviewTable from '../functions/PreviewTable';
// A file tab and a folder tab are one manifest entry rendered inline, which is
// exactly what a View's File card and Folder shelf are, so the tab strip renders
// those two widgets rather than a copy of them.
import FileWidget from '../views/widgets/FileWidget';
import ShelfWidget from '../views/widgets/ShelfWidget';
import BarChartComponent from '../charts/BarChart';
import LineChartComponent from '../charts/LineChart';
import ScatterChartComponent from '../charts/ScatterChart';
import PieChartComponent from '../charts/PieChart';
import BoxAndWhiskerChartComponent from '../charts/BoxAndWhiskerChart';
import SuperChartComponent from '../charts/SuperChart';
import ChartSettingsButton from '../charts/ChartSettingsButton';
import { emailSummaryPending } from '../../utils/runProjection';
import { CHART_DISPLAY_SETTINGS, combinedChildKeys } from '../../utils/chartTheme';
import { countNoun } from '../../utils/pluralise';
import { exportFileName } from '../../utils/excelExport';

const CHART_COMPONENTS = {
  bar: BarChartComponent,
  line: LineChartComponent,
  scatter: ScatterChartComponent,
  pie: PieChartComponent,
  box_and_whisker: BoxAndWhiskerChartComponent,
  superchart: SuperChartComponent,
};

const CHART_SETTINGS_TYPES = {
  bar: 'bar_chart',
  line: 'line_chart',
  scatter: 'scatter_chart',
  pie: 'pie_chart',
  box_and_whisker: 'box_and_whisker_chart',
  superchart: 'superchart',
};

const isPlainObject = value => (
  value !== null && typeof value === 'object' && !Array.isArray(value)
);

// Only PRESENTATION settings are overlaid (CHART_DISPLAY_SETTINGS): the title
// and axis labels are the logic block's words and stay exactly as the run
// produced them, whatever an older save may have stored. A combined chart's
// children are matched by their stable key (combinedChildKeys), never by
// position, so a run that reorders or inserts children keeps each child's own
// settings on it; an older save's positional `children` is ignored.
export const applyChartSettings = (chart, settings) => {
  if (!isPlainObject(chart) || !isPlainObject(settings)) return chart;
  const childSettings = isPlainObject(settings.children_by_key) ? settings.children_by_key : null;
  const displaySettings = {};
  CHART_DISPLAY_SETTINGS.forEach((key) => {
    if (settings[key] !== undefined) displaySettings[key] = settings[key];
  });
  const childKeys = chart.type === 'superchart' ? combinedChildKeys(chart.elements) : [];
  const elements = chart.type === 'superchart' && Array.isArray(chart.elements)
    ? chart.elements.map((child, index) => applyChartSettings(
      child,
      childSettings ? childSettings[childKeys[index]] : null,
    ))
    : chart.elements;
  return {
    ...chart,
    ...displaySettings,
    type: chart.type,
    elements,
  };
};

// A `data` entry's key is almost always the literal string "data" — it names the
// slot, not the thing — so the tab is labelled by the block that PRODUCED it,
// which is what the user named and recognises. `blockNames` comes from the
// published version's blocks; when it is absent (a deleted function, a run of a
// version no longer on disk) the prettified key is all there is to say.
// A chart tab is named by its EFFECTIVE title — the run's title under the
// saved display settings — the same words the chart itself is drawn with.
const tabLabel = (tab, blockNames = null, settingsFor = null) => {
  if (tab.kind === 'chart') {
    const settings = settingsFor ? settingsFor(tab.source_block_id, tab.key) : null;
    return applyChartSettings(tab.chart, settings)?.title || prettifyEntryKey(tab.key);
  }
  if (tab.kind === 'file') return tab.filename || prettifyEntryKey(tab.key);
  // Email status tabs are labelled by the Output node name plus its destination
  // so multiple Email Outputs stay distinguishable. The node name is only present
  // when the backend includes it; otherwise the destination label alone is used
  // and the duplicate-label suffix keeps repeated Email tabs distinct.
  if (tab.kind === 'status') return tab.nodeName ? `${tab.nodeName} · Email` : 'Email';
  if (tab.kind === 'awaiting') {
    if (tab.outputType === 'email') return tab.nodeName ? `${tab.nodeName} · Email` : 'Email';
    return tab.nodeName || tab.outputBlockId;
  }
  if (tab.kind === 'data') {
    const blockName = blockNames?.[tab.source_block_id];
    if (blockName) return formatText(blockName);
  }
  return prettifyEntryKey(tab.key || tab.entry_key) || tab.kind;
};

// Email keeps its own icon whatever it carried: a delivery-status tab is about
// the send, not about the rows inside it.
const tabIcon = tab => {
  if (tab.kind === 'status' || tab.outputType === 'email') return <EmailIcon fontSize="small" />;
  return <EntryTypeIcon kind={tab.kind} filename={tab.filename} mime={tab.mime} />;
};

// Tabs are the union of two sources, because neither alone is complete:
//   - `result.json` carries the ACKNOWLEDGED payload of each Output;
//   - the run's `outputs_summary` carries the live state of EVERY Output,
//     including ones whose payload has not been projected yet and ones that
//     failed before producing anything.
// An Output present only in the summary therefore still gets a visible tab, so a
// Decode UI failure, a pending Email, and a successful table can all be seen at
// once without one Output hiding another.
export const buildCompositeTabs = (outputs, outputsSummary = null) => {
  const outputsMap = isPlainObject(outputs) ? outputs : {};
  const summaryMap = isPlainObject(outputsSummary) ? outputsSummary : {};

  const fromResult = Object.entries(outputsMap).flatMap(([outputBlockId, output]) => {
    const summary = summaryMap[outputBlockId] || null;
    const outputType = output?.output_type || summary?.output_type || null;
    const nodeName = output?.node_name || summary?.node_name || null;
    const produced = Array.isArray(output?.tabs)
      ? output.tabs.map((tab, index) => ({
          ...tab,
          outputBlockId,
          outputType,
          nodeName,
          summary,
          renderKey: `${outputBlockId}:tab:${tab.source_block_id || ''}:${tab.key || ''}:${index}`,
        }))
      : [];
    const skipped = Array.isArray(output?.skipped)
      ? output.skipped.map((entry, index) => ({
          ...entry,
          kind: 'skipped',
          outputBlockId,
          renderKey: `${outputBlockId}:skipped:${entry.source_block_id || ''}:${entry.entry_key || ''}:${index}`,
        }))
      : [];
    // An Output that failed leaves a result object with an error and nothing
    // else. It gets a tab that names it and shows the error, exactly as an
    // Output still awaited does — otherwise the failure would have no tab and,
    // being present in the result map, would also suppress its own summary.
    if (produced.length === 0 && skipped.length === 0 && (output?.error || summary?.status === 'failed')) {
      return [{
        kind: 'awaiting',
        outputBlockId,
        outputType,
        nodeName,
        status: 'failed',
        error: output?.error?.message || output?.error || summary?.error?.message || summary?.error || null,
        summary,
        renderKey: `${outputBlockId}:failed`,
      }];
    }
    return [...produced, ...skipped];
  });

  const awaiting = Object.entries(summaryMap)
    .filter(([outputBlockId]) => !Object.prototype.hasOwnProperty.call(outputsMap, outputBlockId))
    .map(([outputBlockId, summary]) => ({
      kind: 'awaiting',
      outputBlockId,
      outputType: summary?.output_type || null,
      nodeName: summary?.node_name || null,
      status: summary?.status || 'unknown',
      error: summary?.error?.message || summary?.error || null,
      summary,
      renderKey: `${outputBlockId}:awaiting`,
    }));

  return [...fromResult, ...awaiting];
};

// The tab strip mounts one tab at a time, so it keeps the bytes of the files it
// has shown (`bytesCache` in the component) and drops the ones that no longer
// belong to the result on screen: the file of every file tab and the files of
// every folder tab. A run's files live under the run's own storage folder, so a
// path never names another run's file.
export const retainFileBytes = (bytesCache, tabs) => {
  const shownPaths = new Set();
  tabs.forEach((tab) => {
    if (tab.kind === 'file' && tab.gcs_path) shownPaths.add(tab.gcs_path);
    if (tab.kind === 'folder') {
      (tab.files || []).forEach((file) => {
        if (file?.gcs_path) shownPaths.add(file.gcs_path);
      });
    }
  });
  Array.from(bytesCache.keys()).forEach((path) => {
    if (!shownPaths.has(path)) bytesCache.delete(path);
  });
};

// Columns of an Email status row that carry a UTC ISO timestamp. Dates are stored
// as UTC ISO strings by the backend and formatted here in the viewer's local
// timezone (section 3.8).
const STATUS_DATE_COLUMNS = new Set(['Submitted', 'Delivered']);
const ISO_DATETIME_RE = /^\d{4}-\d{2}-\d{2}T/;

const formatStatusCell = (columnName, value) => {
  if (value === null || value === undefined || value === '') return '—';
  const looksIso = typeof value === 'string' && ISO_DATETIME_RE.test(value);
  if (STATUS_DATE_COLUMNS.has(columnName) && looksIso) {
    const stamp = formatTimestamp(value);
    if (stamp) return stamp;
  }
  return String(value);
};

// An Email Output contributes one compact, single-row status result (recipients,
// delivery status, attachments). It is rendered as a small non-paginated table
// using the tab's declared `columns` order; the long `Message` string is retained
// for text-only consumers and shown beneath the table (section 3.8).
function StatusTab({ tab, projectionPending }) {
  const rows = Array.isArray(tab.rows) ? tab.rows : [];
  const columns = Array.isArray(tab.columns) && tab.columns.length > 0
    ? tab.columns
    : Array.from(rows.reduce((set, row) => {
        Object.keys(row || {}).forEach((key) => { if (key !== 'Message') set.add(key); });
        return set;
      }, new Set()));

  if (rows.length === 0) {
    return (
      <Alert severity="info">
        {projectionPending
          ? 'Delivery status for this email has not been projected into the result yet.'
          : 'No delivery status was reported for this output.'}
      </Alert>
    );
  }

  const messages = rows.map((row) => row?.Message).filter(Boolean);

  return (
    <Box sx={{ maxWidth: '100%' }}>
      {projectionPending && (
        <Alert severity="info" sx={{ mb: 1 }}>
          Delivery status is still being updated for this email — the rows below may be behind.
        </Alert>
      )}
      <TableContainer sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {columns.map((col) => (
                <TableCell key={col} sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{col}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, rowIndex) => (
              <TableRow key={rowIndex}>
                {columns.map((col) => (
                  <TableCell key={col}>{formatStatusCell(col, row?.[col])}</TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {messages.length > 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          {messages.join(' ')}
        </Typography>
      )}
    </Box>
  );
}

// A function's saved chart settings are `chart_settings[source_block_id][entry_key]`
// on its head document. Every surface that draws a run's chart is handed that
// map by whoever already listens to the head (the Dashboard's function list,
// a View's per-function watch), so a save shows on every mounted copy at once.
export const chartSettingsFor = (chartSettings, sourceBlockId, entryKey) => {
  const block = isPlainObject(chartSettings) ? chartSettings[sourceBlockId] : null;
  const entry = isPlainObject(block) ? block[entryKey] : null;
  return isPlainObject(entry) ? entry : null;
};

function ChartTab({
  chart,
  functionId,
  companyName,
  sourceBlockId,
  entryKey,
  minHeight,
  savedSettings = null,
}) {
  const renderedChart = useMemo(
    () => applyChartSettings(chart, savedSettings),
    [chart, savedSettings]
  );
  const ChartComponent = CHART_COMPONENTS[renderedChart?.type];

  if (!ChartComponent) {
    return <Alert severity="warning">Unsupported chart type: {renderedChart?.type || 'unknown'}</Alert>;
  }

  return (
    <Box sx={{ position: 'relative', height: minHeight, minHeight }}>
      {companyName && functionId && sourceBlockId && entryKey && (
        <Box sx={{ position: 'absolute', top: 9, right: 70, zIndex: 2 }}>
          <ChartSettingsButton
            functionId={functionId}
            sourceBlockId={sourceBlockId}
            entryKey={entryKey}
            chartSpec={chart}
            chartType={CHART_SETTINGS_TYPES[renderedChart.type]}
            companyName={companyName}
          />
        </Box>
      )}
      <ChartComponent chartData={renderedChart} />
    </Box>
  );
}

function CompositeOutputTabs({
  outputs,
  // The run's complete `outputs_summary`. It is authoritative for which Outputs
  // exist and what state each is in; `outputs` only carries acknowledged
  // payloads.
  outputsSummary = null,
  functionId = null,
  companyName = null,
  // The function's live `chart_settings` map, from the caller's head listener.
  chartSettings = null,
  // "function-name_20260829-1412": what a data tab's Excel export is saved as,
  // with the tab's own label appended. Null keeps the generic filename.
  exportName = null,
  exportHeaders = null,
  chartMinHeight = 420,
  // The height a file tab's viewer is given: the workbook grid needs a definite
  // one. The Dashboard keeps the default; a View's All-tabs card passes its own.
  fileHeight = '60vh',
  // blockId -> block name, from the PUBLISHED version this run executed. Only
  // used to label `data` tabs; absent is a supported state, not an error.
  blockNames = null,
}) {
  const tabs = useMemo(() => buildCompositeTabs(outputs, outputsSummary), [outputs, outputsSummary]);
  // The chosen tab is remembered by identity (renderKey), not position: result
  // hydration, an email-delivery refresh and re-painting the already-selected
  // run each hand in a new `outputs` object with the same tabs, and the choice
  // must survive them. The shown position is derived from the key in render, so
  // a rebuilt list never shows a stale index for a frame; only when the chosen
  // tab no longer exists does the selection fall back to the first tab.
  const [activeKey, setActiveKey] = useState(null);
  // Bytes of the files this result shows, by storage path, shared with the file
  // and folder tabs' viewers. Only the active tab is mounted, so without it every
  // return to a file tab would download the workbook again. The map is held in a
  // ref (filling it must not re-render) and is pruned to the files on screen
  // whenever the tabs change — a different run or a different function.
  const bytesCache = useRef(new Map());
  useEffect(() => {
    retainFileBytes(bytesCache.current, tabs);
  }, [tabs]);
  const settingsFor = (sourceBlockId, entryKey) => chartSettingsFor(chartSettings, sourceBlockId, entryKey);

  if (tabs.length === 0) {
    return <Alert severity="info">No output tabs were produced.</Alert>;
  }

  const shownTab = Math.max(0, tabs.findIndex((tab) => tab.renderKey === activeKey));
  const active = tabs[shownTab];
  const labelOf = (tab) => tabLabel(tab, blockNames, settingsFor);
  const duplicateLabels = tabs.reduce((counts, tab) => {
    const label = labelOf(tab);
    counts[label] = (counts[label] || 0) + 1;
    return counts;
  }, {});
  const seenLabels = {};

  return (
    <Box sx={{ width: '100%', minWidth: 0 }}>
      <Tabs
        value={shownTab}
        onChange={(_, value) => setActiveKey(tabs[value]?.renderKey || null)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
      >
        {tabs.map(tab => {
          const label = labelOf(tab);
          // Two tabs with the same words are told apart by position — "Loss
          // Ratio 1", "Loss Ratio 2" — never by an internal identifier.
          const ordinal = (seenLabels[label] = (seenLabels[label] || 0) + 1);
          return (
            <Tab
              key={tab.renderKey}
              iconPosition="start"
              icon={tabIcon(tab)}
              label={duplicateLabels[label] > 1 ? `${label} ${ordinal}` : label}
            />
          );
        })}
      </Tabs>

      {active.kind === 'data' && (
        <Box sx={{ maxWidth: '100%', overflow: 'auto' }}>
          {Number.isFinite(active.total_rows) && active.total_rows > (active.rows?.length || 0) && (
            <Alert severity="info" sx={{ mb: 1 }}>
              Showing {(active.rows?.length || 0).toLocaleString()} of {countNoun(active.total_rows, 'row')} in this preview.
            </Alert>
          )}
          <PreviewTable
            data={Array.isArray(active.rows) ? active.rows : []}
            columnOrder={Array.isArray(active.columns) ? active.columns : null}
            exportHeaders={exportHeaders}
            filename={exportName ? exportFileName(exportName, labelOf(active)) : null}
          />
        </Box>
      )}

      {active.kind === 'status' && (
        <StatusTab tab={active} projectionPending={emailSummaryPending(active.summary)} />
      )}

      {active.kind === 'awaiting' && (
        <Alert severity={active.status === 'failed' ? 'error' : 'info'}>
          {active.status === 'failed'
            ? `This output failed${active.error ? `: ${active.error}` : '.'}`
            : active.outputType === 'email'
              ? 'This email is still being sent and reconciled. Its delivery status will appear here.'
              : `This output has not produced a result yet (${active.status}).`}
        </Alert>
      )}

      {active.kind === 'chart' && (
        <ChartTab
          key={active.renderKey}
          chart={active.chart}
          functionId={functionId}
          companyName={companyName}
          sourceBlockId={active.source_block_id}
          entryKey={active.key}
          minHeight={chartMinHeight}
          savedSettings={settingsFor(active.source_block_id, active.key)}
        />
      )}

      {active.kind === 'file' && (
        // The file is shown in the viewer, as a View's File card shows it. The
        // workbook grid needs a definite height and the tab strip's content
        // area has none, so the card is given one.
        <Box sx={{ height: fileHeight }}>
          <FileWidget entry={active} bytesCache={bytesCache.current} />
        </Box>
      )}

      {active.kind === 'folder' && (
        // The shelf's rows open each file in its dialog viewer. The list scrolls
        // inside a bounded box so a folder of dozens of files does not stretch
        // the result pane (H82).
        <Box sx={{ maxHeight: '60vh', overflowY: 'auto', pr: 0.5 }}>
          <ShelfWidget entry={active} bytesCache={bytesCache.current} />
        </Box>
      )}

      {active.kind === 'skipped' && (
        <Alert severity="info">
          {active.entry_key || 'This output'} was not produced this run.
        </Alert>
      )}
    </Box>
  );
}

export default CompositeOutputTabs;
